import { Observable, TimeoutError, retry, throwError, timeout, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';

import { NetworkService } from '@core/services/network/network.service';

/** Per-request timeout override (ms). Default: 25 000 ms. */
export const HTTP_TIMEOUT = new HttpContextToken<number>(() => 25_000);

const IDEMPOTENT_METHODS = ['GET', 'HEAD', 'OPTIONS'];

export const timeoutInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  if (!request.url.includes(environment.apiBaseUrl)) {
    return next(request);
  }

  const network = inject(NetworkService);
  const timeoutMs = request.context.get(HTTP_TIMEOUT);
  const isIdempotent = IDEMPOTENT_METHODS.includes(request.method);

  let source$ = next(request).pipe(
    timeout(timeoutMs),
    catchError((error: unknown) => {
      if (error instanceof TimeoutError) {
        const requestId = request.headers.get('X-Request-ID') || 'unknown';

        console.error(
          `[HTTP Timeout] ${request.method} ${request.urlWithParams} (X-Request-ID: ${requestId}) timed out after ${timeoutMs}ms`
        );

        return throwError(
          () =>
            new HttpErrorResponse({
              error,
              status: 0,
              statusText: `Request Timeout (${timeoutMs}ms)`,
              url: request.urlWithParams,
            })
        );
      }
      return throwError(() => error);
    })
  );

  // Retry idempotent requests with exponential backoff (2s, 4s).
  // Each retry re-subscribes to the full inner chain (auth, timeout, etc.)
  if (isIdempotent) {
    source$ = source$.pipe(
      retry({
        count: 2,
        delay: (error, attempt) => {
          if (!network.isOnline()) {
            return throwError(() => error);
          }
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return throwError(() => error);
          }
          console.warn(
            `[HTTP Retry] ${request.method} ${request.urlWithParams} — attempt ${attempt + 1}`
          );
          return timer(attempt * 2000);
        },
      })
    );
  }

  return source$;
};
